import {useQuery} from '@tanstack/react-query';
import {useAuthContext} from '../context/AuthContext';
import {useTeamsContext} from '../context/TeamsContext';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch';
import baseUrl from '../api/baseUrl';
import {handleResponse} from '../api/handleResponse';

type VoiceAssistantAvailabilityResponse = {
    voiceAssistantEnabled: boolean;
    /** Tenant has installed a Teams package that grants microphone access. */
    voiceEnabledInTeams?: boolean;
    /** Effective idle timeout in seconds; 0 means never close on silence. */
    idleTimeoutSeconds?: number;
    /** "OpenAI" for GPT-Live or "Azure OpenAI" for the Realtime fallback. */
    provider?: string;
};

/**
 * Shown wherever the user meets the voice feature when the tenant runs on the
 * Azure Realtime fallback, which copes with background noise less well than
 * GPT-Live. Kept in one place so the panel, the sheet, and the admin page say
 * the same thing.
 */
export const azureVoiceProviderNote =
    'Voice on this tenant runs on the Azure OpenAI Realtime model. It handles background noise less well than GPT-Live, so it works best in a quiet environment.';

const defaultIdleTimeoutSeconds = 60;

const useVoiceAssistantAvailability = () => {
    const {isAuthenticated} = useAuthContext();
    const {isRunningInTeams, supportsLiveMicrophone} = useTeamsContext();
    const authenticatedFetch = useCreateAuthenticatedFetch();

    const {data, error, isPending} = useQuery({
        queryKey: ['voiceAssistantAvailability', isAuthenticated],
        enabled: isAuthenticated,
        queryFn: async (): Promise<VoiceAssistantAvailabilityResponse> => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/voice/availability`
            );
            return handleResponse(response);
        }
    });

    return {
        voiceAssistantEnabled:
            (data?.voiceAssistantEnabled ?? false) &&
            (!isRunningInTeams ||
                (supportsLiveMicrophone &&
                    (data?.voiceEnabledInTeams ?? false))),
        idleTimeoutSeconds:
            data?.idleTimeoutSeconds ?? defaultIdleTimeoutSeconds,
        provider: data?.provider ?? '',
        providerNote:
            data?.provider === 'Azure OpenAI' ? azureVoiceProviderNote : null,
        error,
        isLoading: isAuthenticated && isPending
    };
};

export default useVoiceAssistantAvailability;
